import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import "../../App.css";
import BACKEND_URL from "../../config/config";
import { persistFeatureFlags, resolveFeatureFlags } from "../../config/featureFlags";
import { formatMoney } from "../../config/publicConfig";
import EconoJournalLogo from "../../components/EconoJournalLogo";
import { showPopup } from "../../utils/popup";
import { useLanguage } from "../../context/LanguageContext";

export default function Visualizar() {
  const getStoredValue = (key, defaultValue = '') => sessionStorage.getItem(key) || defaultValue;
  const formatEmissionValue = (value) => {
    const number = Number(value);
    return Number.isFinite(number)
      ? number.toLocaleString("es-AR", { maximumFractionDigits: 2 })
      : "0";
  };
  const getEmissionValue = (data, keys) => {
    for (const key of keys) {
      const value = key
        .split(".")
        .reduce((current, part) => current?.[part], data);
      const number = Number(value);

      if (Number.isFinite(number)) return number;
    }

    return null;
  };
  const persistEmissionBreakdown = (data) => {
    const air = getEmissionValue(data, [
      "co2_air_user",
      "co2_air",
      "air_emission",
      "air_emissions",
      "emissions.co2_air_user",
      "emissions.air",
    ]);
    const land = getEmissionValue(data, [
      "co2_land_user",
      "co2_transport_user",
      "co2_land",
      "land_emission",
      "land_emissions",
      "emissions.co2_land_user",
      "emissions.land",
    ]);
    const hotel = getEmissionValue(data, [
      "co2_hotel_user",
      "co2_hotel",
      "hotel_emission",
      "hotel_emissions",
      "emissions.co2_hotel_user",
      "emissions.hotel",
    ]);

    if (air !== null) sessionStorage.setItem("co2_air_user", String(air));
    if (land !== null) sessionStorage.setItem("co2_land_user", String(land));
    if (hotel !== null) sessionStorage.setItem("co2_hotel_user", String(hotel));
  };
  const { language } = useLanguage();
  const t = {
    EN: {
      summary: "Data summary",
      yourData: "Your data / Edit",
      editData: "Edit data",
      fullName: "Full name:",
      phone: "Phone:",
      company: "Company:",
      emissionsDetail: "Emissions detail",
      totalEmitted: "Total emitted:",
      source: "Emission source",
      consumption: "Consumption data",
      emission: "Emission",
      plane: "Plane to CABA",
      hotel: "Hotel",
      no: "No",
      nights: "night(s)",
      registrationCost: "Registration fee:",
      registration: "Registration:",
      confirmPay: "Confirm and pay",
      confirm: "Confirm",
      footer: "Design, code, words, and hard work by ",
      paymentUnavailable:
        "Online payment is not available right now. Please try again later or contact us for help.",
      paymentUnavailableTitle: "Payment unavailable",
      continueError: "We couldn't continue",
      saveError: "We couldn't save your registration. Please try again.",
      configError: "Could not load the event configuration.",
    },
    ES: {
      summary: "Resumen de datos",
      yourData: "Tus datos / Editar",
      editData: "Editar datos",
      fullName: "Nombre completo:",
      phone: "Teléfono:",
      company: "Compañía:",
      emissionsDetail: "Detalle de emisiones",
      totalEmitted: "Total emitido:",
      source: "Fuente de emisión",
      consumption: "Dato consumo",
      emission: "Emisión",
      plane: "Avión hasta CABA",
      hotel: "Hotel",
      no: "No",
      nights: "noche(s)",
      registrationCost: "Costo de inscripción:",
      registration: "Inscripción:",
      confirmPay: "Confirmar y pagar",
      confirm: "Confirmar",
      footer: "Design, code, words, and hard work by ",
      paymentUnavailable:
        "La opción de pago online no está disponible en este momento. Intentá nuevamente más tarde o contactanos para recibir ayuda.",
      paymentUnavailableTitle: "Pago no disponible",
      continueError: "No pudimos continuar",
      saveError: "No pudimos guardar tu inscripción. Intentá nuevamente.",
      configError: "No se pudo cargar la configuración del evento.",
    },
  };
  const invoiceText = {
    EN: {
      beforeEmail:
        "If you require an invoice, please request it by email as soon as you complete the purchase at",
      afterEmail:
        "until August 30. Please make the request before the event, including your registration number and, if it is Invoice A, we also need Tax ID (CUIT) and legal business name. Thank you for your participation!",
    },
    ES: {
      beforeEmail:
        "Si requerís Factura, te pedimos que la solicites vía email apenas concretes la compra a",
      afterEmail:
        "hasta el 30 de agosto. Por favor realizá el pedido con anticipación al evento, indicando número de registro y si es Factura A, además, necesitamos CUIT y razón social. Gracias por tu participación!",
    },
  };

  const [fullName] = useState(getStoredValue('full_name'));
  const [dni] = useState(getStoredValue("dni"));
  const [email] = useState(getStoredValue('email'));
  const [phone] = useState(getStoredValue('phone'));
  const [company] = useState(getStoredValue('company_name'));
  const travelTime = getStoredValue('travel_time');
  const eventTransport = getStoredValue('event_transport');
  const kmLand = getStoredValue('km_land');
  const stayedInHotel = getStoredValue('stayed_in_hotel');
  const hotelNights = getStoredValue('hotel_nights');

  const codeIsValid = getStoredValue("code_is_valid") === "true";

  const co2AirUser = getStoredValue('co2_air_user');
  const co2LandUser = getStoredValue('co2_land_user');
  const co2HotelUser = getStoredValue('co2_hotel_user');
  const co2TotalPerUser = getStoredValue('co2_total_per_user');


  const navigate = useNavigate();

  useEffect(() => {
    sessionStorage.setItem("edit_mode", "true");
  }, []);

  
  const [publicConfig, setPublicConfig] = useState(null);
  const [publicConfigError, setPublicConfigError] = useState("");
  const [isLoadingPublicConfig, setIsLoadingPublicConfig] = useState(true);

  const storedCurrency = (sessionStorage.getItem("currency") || "ARS").toUpperCase();
  const storedFinalPrice = Number(sessionStorage.getItem("final_price") || 0);

  const currency = (publicConfig?.currency || storedCurrency || "ARS").toUpperCase();
  const { IS_FREE, ENABLE_EMISSION } = resolveFeatureFlags(publicConfig || {});

  const basePrice = Number(
    publicConfig?.price_amount !== undefined && publicConfig?.price_amount !== null
      ? publicConfig.price_amount
      : publicConfig?.price !== undefined && publicConfig?.price !== null
      ? publicConfig.price
      : storedFinalPrice
  );

  const [finalPrice, setFinalPrice] = useState(() => {
    return storedFinalPrice || basePrice || 0;
  });


  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setIsLoadingPublicConfig(true);
        setPublicConfigError("");

        const res = await fetch(`${BACKEND_URL}/public-config`);
        if (!res.ok) throw new Error("No se pudo cargar public-config");

        const data = await res.json();
        if (cancelled) return;

        persistFeatureFlags(data);
        setPublicConfig(data);
      } catch (e) {
        if (!cancelled) {
          setPublicConfig(null);
          setPublicConfigError("No se pudo cargar la configuración del evento.");
        }
      } finally {
        if (!cancelled) setIsLoadingPublicConfig(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);


  useEffect(() => {

  sessionStorage.setItem("currency", currency);

  if (IS_FREE || codeIsValid) {
    setFinalPrice(0);
    sessionStorage.setItem("final_price", "0");
    return;
  }

  if (!basePrice || Number.isNaN(basePrice) || basePrice <= 0) {
    return;
  }

  setFinalPrice(basePrice);
  sessionStorage.setItem("final_price", String(basePrice));
}, [IS_FREE, codeIsValid, basePrice, currency]);



  const collectPayload = () => {
  const pick = (k, def = "") => sessionStorage.getItem(k) || def;
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const userId = sessionStorage.getItem("user_id"); 
  const codeIsValidNow = sessionStorage.getItem("code_is_valid") === "true";

  const hotelFlag = toNum(pick("stayed_in_hotel", "0"));
  const nights = toNum(pick("hotel_nights", "0"));

  return {
    id_user_response: userId ? Number(userId) : undefined, 
    first_name: pick("first_name"),
    last_name: pick("last_name"),
    dni: pick("dni"),
    email: pick("email"),
    phone: pick("phone"),
    position: pick("position"),
    region: pick("region"),
    sector: pick("sector"),
    employees: pick("employees"),
    id_company: pick("id_company") ? parseInt(pick("id_company"), 10) : null,
    company_name: pick("company_name") || null,
    event_source: pick("event_source"),
    event_source_other: pick("event_source_other"),

    //   Solo mandamos invitation_code si NO es free y el código es válido
    invitation_code: (!IS_FREE && codeIsValidNow)
      ? (pick("invitation_code") || "").toUpperCase().trim()
      : "",

    panel_topics: pick("panel_topics"),
    //   Emissions: si no existen (USA), mandamos defaults seguros
    air_travel: pick("air_travel", "0"),
    travel_time: toNum(pick("travel_time", "0")),
    event_transport: pick("event_transport", "N/A"),
    km_land: toNum(pick("km_land", "0")),
    stayed_in_hotel: hotelFlag ? 1 : 0,
    hotel_nights: hotelFlag ? nights : 0,

    extra_answers: {
      questions_emissions_1: pick("questions_emissions_1"),
      questions_emissions_2: pick("questions_emissions_2"),
      questions_emissions_3: pick("questions_emissions_3"),
      questions_ECJ_1: pick("questions_ECJ_1"),
      questions_ECJ_1_other: pick("questions_ECJ_1_other"),
      questions_ECJ_2: pick("questions_ECJ_2"),
      questions_ECJ_3: pick("questions_ECJ_3"),
      questions_ECJ_4: pick("questions_ECJ_4"),
      questions_ECJ_4_other: pick("questions_ECJ_4_other"),
    },

    inscripto: false,
  };
};

const saveResponseOrFail = async () => {
  const payload = collectPayload();

  const res = await fetch(`${BACKEND_URL}/responses`, {
    method: "POST", 
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const data = await res.json().catch(() => ({}));
  console.log("[responses] status", res.status, "payload:", payload, "response:", data);

  if (!res.ok || data?.status === "error") {
    const msg = data?.code || data?.message || data?.error || "No pudimos guardar tu inscripción.";
    throw new Error(msg);
  }

  if (data?.user_id) {
    sessionStorage.setItem("user_id", String(data.user_id));
    localStorage.setItem("user_id", String(data.user_id));
  }

  const email = (sessionStorage.getItem("email") || "").trim();
  if (email) localStorage.setItem("email", email);

  const co2 =
    data?.co2_total_per_user ??
    data?.co2_emitted_user ??
    data?.emissions_kg_co2e;
  const co2Number = Number(co2);

  if (Number.isFinite(co2Number)) {
    localStorage.setItem("emissions_kg_co2e", String(co2Number));
    sessionStorage.setItem("co2_total_per_user", String(co2Number));
  }
  persistEmissionBreakdown(data);

  return data;
};

 const handleConfirm = async () => {
  const codeIsValidNow = sessionStorage.getItem("code_is_valid") === "true";
  console.log("[flow] IS_FREE", IS_FREE, "codeIsValidNow", codeIsValidNow);

  try {
    //   SIEMPRE guardar/actualizar en DB antes de decidir pago/thanks
    await saveResponseOrFail();

    const userId = sessionStorage.getItem("user_id");
    const emailNow = sessionStorage.getItem("email") || "";

    // 1) Free global => thanks
    if (IS_FREE) {
      sessionStorage.removeItem("edit_mode");
      navigate("/thanks");
      return;
    }

    // 2) Invitado => confirmar invitado y thanks
    if (codeIsValidNow) {
      try {
        await fetch(`${BACKEND_URL}/confirm-invited`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_user_response: userId, email: emailNow }),
        });
      } catch (e) {
        console.error("confirm-invited error:", e);
      }
      sessionStorage.removeItem("edit_mode");
      navigate("/thanks");
      return;
    }

    // 3) Pago
    const res = await fetch(`${BACKEND_URL}/start-payment`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: emailNow,
        id_user_response: Number(userId),
      }),
    });

    if (!res.ok) throw new Error(await res.text());
    const data = await res.json();

    const redirectUrl =
      data?.payment_url ||
      data?.init_point ||
      data?.preference_url ||
      data?.approval_url;

    if (typeof redirectUrl === "string") {
      sessionStorage.removeItem("edit_mode");
      window.location.href = redirectUrl;
      return;
    }

    showPopup(
      "La opción de pago online no está disponible en este momento. Intentá nuevamente más tarde o contactanos para recibir ayuda.",
      {
        title: "Pago no disponible",
        type: "error",
      }
    );
    console.error("start-payment unexpected:", data);
  } catch (error) {
    showPopup(
      String(error?.message || "No pudimos guardar tu inscripción. Intentá nuevamente."),
      {
        title: "No pudimos continuar",
        type: "error",
      }
    );
    console.error(error);
  }
};


  const showPaymentBlock = !IS_FREE && !codeIsValid;

  const hotelLabel =
    stayedInHotel === "1"
      ? `${hotelNights || "1"} ${t[language].nights}`
      : t[language].no;

  return (
    <div className='container'>
      <div className='containerFlex'>
        <div className='card flexBetween'>
          <div>
            <EconoJournalLogo className="logoECJ" />
          </div>
          <div className='containerFlex alignCenter'>
            <h2>{t[language].summary}</h2>
          </div>
        </div>
      </div>

      <div className='card'>
        <div className='flexStart'>
          <h2>{t[language].yourData}</h2>
          <Link to='/'>
            <img className='editIcon' src="/img/edit.svg" alt={t[language].editData} />
          </Link>
        </div>

        <p><b>{t[language].fullName} </b> {fullName}</p>
        <p><b>Email:</b> {email}</p>
        <p><b>{t[language].phone}</b> {phone}</p>
        <p><b>{t[language].company} </b> {company}</p>
      </div>

      {ENABLE_EMISSION && (
        <div className='card'>
          <div className='flexBetween'>
            <div className='flexStart'>
              <h2>{t[language].emissionsDetail}</h2>
              <Link to='/emission'>
                <img className='editIcon' src="/img/edit.svg" alt={t[language].editData} />
              </Link>
            </div>

            <div className='flexStart'>
              <h2>{t[language].totalEmitted}</h2>
              <div className='parcialEmission'>
                {co2TotalPerUser ? `${formatEmissionValue(co2TotalPerUser)} kg CO2e` : 'Kg CO₂e'}
              </div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>{t[language].source}</th>
                <th>{t[language].consumption}</th>
                <th>{t[language].emission}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t[language].plane}</td>
                <td>{travelTime} Hs.</td>
                <td>{formatEmissionValue(co2AirUser)} Kg CO₂e</td>
              </tr>
              <tr>
                <td>{eventTransport}</td>
                <td>{kmLand}</td>
                <td>{formatEmissionValue(co2LandUser)} Kg CO₂e</td>
              </tr>
              <tr>
                <td>{t[language].hotel}</td>
                <td>{hotelLabel}</td>
                <td>{formatEmissionValue(co2HotelUser)} Kg CO₂e</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className='card flexBetween'>
        <div>
          <h2>{showPaymentBlock ? t[language].registrationCost : t[language].registration}</h2>


          <div className='totalEmission'>
            {formatMoney(finalPrice, currency)}
          </div>

          {showPaymentBlock && (
            <p className="legal">
              {invoiceText[language].beforeEmail}{" "}
              <a href="mailto:administracion@econojournal.com.ar">
                administracion@econojournal.com.ar
              </a>{" "}
              <b>{invoiceText[language].afterEmail}</b>
            </p>
          )}
        </div>

        <div>
          <button onClick={handleConfirm}>
            {showPaymentBlock ? t[language].confirmPay : t[language].confirm}
          </button>
        </div>
      </div>

      <h4 className="footerCopyright">
        {t[language].footer}
        <Link to="https://wcm.media" target="_blank" rel="noreferrer">Wildcat Media.</Link>
      </h4>
    </div>
  );
}
